import { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { WalletOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { MODE_META, fmt } from "./paymentModeMeta";
import PaymentModeDialog from "./PaymentModeDialog";

/* ════════════════════════════════════════════════════════════════
   DATA LAYER  (no UI here)
   ----------------------------------------------------------------
   Source : SINGLE_ENTRY_DATA  (retail ERP API)
   Scope  : TODAY ONLY — filtered on the server with a WHERE clause AND
            re-checked on the client (so even if the server ignores the
            WHERE, old rows can never leak into the totals).

   How the rows are read (from your sample data):
   - STYPE "CASH BOOK"  → money through the cash drawer
        JAMAAMOUNT  = cash received  (inward)
        NAMAAMOUNT  = cash paid out  (outward)         ⚠ assumed
   - STYPE "BANK TRANS" → money through a bank / UPI / card account
        NAMAAMOUNT  = amount received (inward)  e.g. 480 @ GPAY
        JAMAAMOUNT  = amount paid out (outward)        ⚠ assumed
        The mode comes from PAYMODE (GPAY, CARD, CHEQUE, NEFT …); if
        PAYMODE is "-" it falls back to the "@ GPAY -" text inside
        PARTICULARS.
   - "CASH SALES" / "CARD SALES" / customer rows are the sales side of the
     same bill — ignored, otherwise every bill would be counted twice.
   - OPENING BALANCE rows are ignored.

   ⚠ CONFIRM — the outward side (payments / expenses) isn't in your sample
   (it only had sales), so it's assumed to mirror inward with the columns
   swapped. If outward looks wrong, swap JAMAAMOUNT / NAMAAMOUNT below.
   ════════════════════════════════════════════════════════════════ */
const API_BASE = "https://retailtextiles.timeserasoftware.in";

const SOURCE = {
  table: "SINGLE_ENTRY_DATA",
  dateCol: "ENTRYDATE",
};

const MODE_KEYS = ["CASH", "CARD", "UPI", "CHEQUE", "ONLINE"];

const emptyModes = () =>
  MODE_KEYS.map((key) => ({
    mode: key,
    inward: 0,
    outward: 0,
    label: MODE_META[key]?.label || key,
  }));

/* map a raw PAYMODE text onto one of the 5 modes shown in the card */
const normalizeMode = (raw) => {
  const t = String(raw || "").toUpperCase();
  if (!t || t === "-") return null;
  if (/GPAY|G PAY|PHONEPE|PAYTM|BHIM|UPI/.test(t)) return "UPI";
  if (/CARD|POS|SWIPE|DEBIT|CREDIT/.test(t)) return "CARD";
  if (/CHEQUE|CHQ|CHECK/.test(t)) return "CHEQUE";
  return "ONLINE"; // NEFT / RTGS / IMPS / ONLINE / anything else bank-side
};

/* PAYMODE first, then the "@ GPAY -" token inside PARTICULARS */
const resolveBankMode = (row) => {
  const direct = normalizeMode(row.PAYMODE);
  if (direct) return direct;
  const m = String(row.PARTICULARS || "").match(/@\s*([A-Za-z ]+?)\s*-/);
  return normalizeMode(m?.[1]) || "ONLINE";
};

const aggregateSingleEntry = (rows, todayKey) => {
  const totals = {};
  MODE_KEYS.forEach((k) => (totals[k] = { inward: 0, outward: 0 }));

  rows.forEach((row) => {
    // today only (client-side safety net)
    if (String(row[SOURCE.dateCol] || "").slice(0, 10) !== todayKey) return;

    if (String(row.TRANS_TYPE || "").toUpperCase().includes("OPENING")) return;

    const stype = String(row.STYPE || "").trim().toUpperCase();
    const jama = Number(row.JAMAAMOUNT || 0);
    const nama = Number(row.NAMAAMOUNT || 0);

    if (stype === "CASH BOOK") {
      totals.CASH.inward += jama;
      totals.CASH.outward += nama;
    } else if (stype === "BANK TRANS") {
      const mode = resolveBankMode(row);
      totals[mode].inward += nama;
      totals[mode].outward += jama;
    }
  });

  return totals;
};

const rowVariant = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.07, duration: 0.35, ease: "easeOut" },
  }),
};

/* infinite gentle nudge to signal the arrow is clickable */
const arrowNudge = {
  animate: {
    x: [0, 4, 0],
    transition: { duration: 1.1, repeat: Infinity, ease: "easeInOut" },
  },
};

const PaymentOverview = () => {
  const [modes, setModes] = useState(emptyModes());
  const tenantName = localStorage.getItem("tenantName");

  /* dialog state — which mode's "View" was clicked */
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("CASH");

  const openDialog = (modeKey) => {
    setDialogMode(modeKey);
    setDialogOpen(true);
  };

  useEffect(() => {
    const load = async () => {
      try {
        // computed here (not at module level) so it's always the real "today"
        const todayKey = dayjs().format("YYYY-MM-DD");
        const nextKey = dayjs().add(1, "day").format("YYYY-MM-DD");
        const where = `${SOURCE.dateCol}>='${todayKey}' AND ${SOURCE.dateCol}<'${nextKey}'`;

        const res = await axios.get(
          `${API_BASE}/api/ERP/GetDataFromGivenTableNameWithWhere`,
          {
            params: { tableName: SOURCE.table, where },
            headers: tenantName ? { tenantName } : {},
          },
        );
        const rows = Array.isArray(res.data) ? res.data : [];
        const totals = aggregateSingleEntry(rows, todayKey);

        setModes(
          MODE_KEYS.map((key) => ({
            mode: key,
            inward: totals[key].inward,
            outward: totals[key].outward,
            label: MODE_META[key]?.label || key,
          })),
        );
      } catch (e) {
        // No more dummy numbers on failure — show zeros so wrong data is
        // never mistaken for real data.
        console.error("PaymentOverview:", e);
        setModes(emptyModes());
      }
    };
    load();
    // const id = setInterval(load, 5000);
    // return () => clearInterval(id);
  }, [tenantName]);

  const totalInward = modes.reduce((a, m) => a + m.inward, 0);
  const totalOutward = modes.reduce((a, m) => a + m.outward, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
          flexShrink: 0,
        }}
      >
        {/* Title */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#2d1b69" }}>
            Payment Overview
          </div>
          <div style={{ fontSize: 10, color: "#aaa", marginTop: 1 }}>
            Today's transactions
          </div>
        </div>

        {/* IN / OUT summary badges */}
        <div style={{ display: "flex", gap: 8 }}>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
            style={{
              background: "#eafff8",
              borderRadius: 12,
              padding: "6px 14px",
              textAlign: "center",
              minWidth: 70,
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: "#43aa8b",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              IN
            </div>
            <div style={{ fontSize: 14, fontWeight: 900, color: "#43aa8b", lineHeight: 1.2 }}>
              {fmt(totalInward)}
            </div>
          </motion.div>

          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.18, type: "spring", stiffness: 200 }}
            style={{
              background: "#fff0f0",
              borderRadius: 12,
              padding: "6px 14px",
              textAlign: "center",
              minWidth: 70,
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: "#e05252",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              OUT
            </div>
            <div style={{ fontSize: 14, fontWeight: 900, color: "#e05252", lineHeight: 1.2 }}>
              {fmt(totalOutward)}
            </div>
          </motion.div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "44px 1fr 70px 70px",
          alignItems: "center",
          padding: "4px 4px 6px",
          borderBottom: "1.5px solid #ede5ff",
          marginBottom: 4,
          flexShrink: 0,
        }}
      >
        <div />
        <div style={{ fontSize: 9, fontWeight: 800, color: "#aaa", textTransform: "uppercase", letterSpacing: 0.8 }}>
          Mode
        </div>
        <div style={{ fontSize: 9, fontWeight: 800, color: "#43aa8b", textTransform: "uppercase", letterSpacing: 0.8, textAlign: "right" }}>
          Inward
        </div>
        <div style={{ fontSize: 9, fontWeight: 800, color: "#e05252", textTransform: "uppercase", letterSpacing: 0.8, textAlign: "right" }}>
          Outward
        </div>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {modes.map((m, i) => {
          const meta = MODE_META[m.mode] || {
            icon: <WalletOutlined />,
            label: m.label,
            iconBg: "#4c3494",
            iconColor: "#e8e4ff",
          };

          return (
            <motion.div
              key={m.mode}
              custom={i}
              variants={rowVariant}
              initial="hidden"
              animate="visible"
              whileHover={{ backgroundColor: "#f8f6ff" }}
              style={{
                display: "grid",
                gridTemplateColumns: "44px 1fr 70px 70px",
                alignItems: "center",
                padding: "6px 4px",
                borderRadius: 10,
                cursor: "default",
                transition: "background 0.18s",
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 11,
                  background: meta.iconBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  color: meta.iconColor,
                  boxShadow: `0 4px 12px ${meta.iconBg}55`,
                  flexShrink: 0,
                }}
              >
                {meta.icon}
              </div>

              {/* Label + inline View button */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  paddingLeft: 10,
                  minWidth: 0,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: "#2d1b69", flexShrink: 0 }}>
                  {meta.label}
                </span>

                <motion.button
                  onClick={() => openDialog(m.mode)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.96 }}
                  style={{
                    border: "none",
                    background: `${meta.iconBg}1c`,
                    color: meta.iconBg,
                    padding: "3.5px 9px 3.5px 10px",
                    borderRadius: 20,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    cursor: "pointer",
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: 0.3,
                    flexShrink: 0,
                  }}
                >
                  View
                  <motion.span
                    variants={arrowNudge}
                    animate="animate"
                    style={{ display: "inline-flex", fontSize: 10 }}
                  >
                    <ArrowRightOutlined />
                  </motion.span>
                </motion.button>
              </div>

              {/* Inward */}
              <div style={{ fontSize: 12, fontWeight: 800, color: "#43aa8b", textAlign: "right" }}>
                {fmt(m.inward)}
              </div>

              {/* Outward */}
              <div style={{ fontSize: 12, fontWeight: 800, color: "#e05252", textAlign: "right" }}>
                {fmt(m.outward)}
              </div>
            </motion.div>
          );
        })}
      </div>

      <PaymentModeDialog
        open={dialogOpen}
        mode={dialogMode}
        onClose={() => setDialogOpen(false)}
      />
    </motion.div>
  );
};

export default PaymentOverview;